import { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { Award, CalendarDays, Medal, Trophy } from 'lucide-react-native';
import { useQuery } from '@tanstack/react-query';
import { StyleSheet } from 'react-native-unistyles';
import { getAchievementHighlightPriority } from '@shared/achievements';
import { rankSeasonStandings, type SeasonStandingMetric } from '@shared/ranking';

import { Avatar, AvatarFallback, AvatarImage } from './ui/Avatar';
import { AchievementBadge, AchievementCollection, type AchievementCollectionData, type Earned } from './achievements/AchievementCollection';
import { useAuth } from '../hooks/useAuth';
import { apiRequest } from '../lib/queryClient';
import { useTRPC } from '../lib/trpc';
import { colors, typography } from '@/lib/theme';

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profileImageUrl: string | null;
}

interface WeeklyStanding {
  week: number;
  points: number;
  correctPicks: number;
  totalPicks: number;
}

interface LeaderboardEntry {
  userId: string;
  user: User;
  weeklyStandings: WeeklyStanding[];
  totalPoints: number;
  totalCorrectPicks: number;
  totalPicks: number;
  tiebreakerDelta?: number | null;
}

interface LeaderboardProps {
  leagueId: string;
  seasonId?: string | null;
  scoringSystem?: 'oddsBased' | 'totalWins';
}

interface AchievementHighlightsPayload {
  supported: boolean;
  catalog: AchievementCollectionData['catalog'];
  earned: (Earned & { userId: string })[];
  weeklyWinners: { leagueWeekId: string; label: string; weekNumber: number; winners: { userId: string; points: number; correctPicks: number; totalPicks: number }[] }[];
}

function displayName(user: User) {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  return user.firstName || user.email?.split('@')[0] || 'Player';
}

function initials(user: User) {
  return `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || 'P';
}

export default function Leaderboard({ leagueId, seasonId, scoringSystem = 'oddsBased' }: LeaderboardProps) {
  const { user } = useAuth();
  const trpc = useTRPC();
  const showPointsSort = scoringSystem !== 'totalWins';
  const [requestedSort, setRequestedSort] = useState<SeasonStandingMetric>('points');
  const activeSort: SeasonStandingMetric = showPointsSort ? requestedSort : 'correct';

  const { data: leaderboard, isLoading: leaderboardLoading, isError: leaderboardError } = useQuery<LeaderboardEntry[]>({
    queryKey: [`/api/leagues/${leagueId}/leaderboard`, seasonId],
    enabled: Boolean(leagueId && seasonId),
    queryFn: async () => {
      const response = await apiRequest(`/api/leagues/${leagueId}/leaderboard?seasonId=${seasonId}`, 'GET');
      if (!response.ok) throw new Error('Failed to fetch league results');
      return response.json();
    },
  });

  const ownAchievementQuery = useQuery(
    trpc.leagues.achievements.queryOptions(
      { leagueId, seasonId: seasonId ?? '', userId: user?.id ?? '' },
      { enabled: Boolean(leagueId && seasonId && user?.id) },
    ),
  );

  const highlightsQuery = useQuery(
    trpc.leagues.achievementHighlights.queryOptions(
      { leagueId, seasonId: seasonId ?? '' },
      { enabled: Boolean(leagueId && seasonId) },
    ),
  );

  const weeklyWinners = useMemo(() => {
    const payload = highlightsQuery.data as AchievementHighlightsPayload | undefined;
    if (!payload?.supported || !leaderboard?.length) return [];
    const usersById = new Map(leaderboard.map((entry) => [entry.userId, entry]));
    return [...payload.weeklyWinners]
      .sort((a, b) => b.weekNumber - a.weekNumber)
      .flatMap((week) => {
        const winners = week.winners.flatMap((standing) => {
          const entry = usersById.get(standing.userId);
          return entry ? [{ entry, standing }] : [];
        });
        return winners.length ? [{ id: week.leagueWeekId, label: week.label, winners }] : [];
      });
  }, [highlightsQuery.data, leaderboard]);

  const highlightItems = useMemo(() => {
    const payload = highlightsQuery.data as AchievementHighlightsPayload | undefined;
    if (!payload?.supported || !leaderboard?.length || !user?.id) return [];
    const usersById = new Map(leaderboard.map((entry) => [entry.userId, entry.user]));
    const badgesByKey = new Map(payload.catalog.map((badge) => [badge.key, badge]));
    const newestByPlayerAndBadge = new Map<string, AchievementHighlightsPayload['earned'][number]>();
    for (const award of payload.earned) {
      if (award.userId === user.id || !usersById.has(award.userId)) continue;
      const key = `${award.userId}:${award.badgeKey}`;
      const existing = newestByPlayerAndBadge.get(key);
      const timestamp = (value: Date | string | null) => value ? new Date(value).getTime() : 0;
      if (!existing || timestamp(award.awardedAt) >= timestamp(existing.awardedAt)) {
        newestByPlayerAndBadge.set(key, award);
      }
    }
    return [...newestByPlayerAndBadge.values()]
      .map((award) => ({ award, badge: badgesByKey.get(award.badgeKey), player: usersById.get(award.userId)! }))
      .filter((item): item is typeof item & { badge: NonNullable<typeof item.badge> } => Boolean(item.badge))
      .sort((a, b) => {
        const priorityDelta = getAchievementHighlightPriority(a.award.badgeKey) - getAchievementHighlightPriority(b.award.badgeKey);
        if (priorityDelta !== 0) return priorityDelta;
        return new Date(b.award.awardedAt ?? 0).getTime() - new Date(a.award.awardedAt ?? 0).getTime();
      })
      .slice(0, 6);
  }, [highlightsQuery.data, leaderboard, user?.id]);

  const ownAchievements = ownAchievementQuery.data as AchievementCollectionData | undefined;
  const isLoading = !seasonId || leaderboardLoading || highlightsQuery.isLoading;
  const seasonStandings = useMemo(() => {
    if (!leaderboard?.length) return [];
    return rankSeasonStandings(
      leaderboard.map((entry) => ({
        ...entry,
        totalCorrectPicks: entry.totalCorrectPicks ?? 0,
        totalPicks: entry.totalPicks ?? 0,
        totalPoints: entry.totalPoints ?? 0,
      })),
      activeSort,
    );
  }, [activeSort, leaderboard]);

  return (
    <View style={styles.page}>
      <AchievementCollection
        data={ownAchievements}
        isLoading={!user?.id || ownAchievementQuery.isLoading}
        error={ownAchievementQuery.isError}
        playerName="Your"
      />

      <View style={styles.section} testID="season-standings">
        <View style={styles.sectionHeader}>
          <View style={styles.sectionIcon}><Medal size={19} color={colors.ink.field} /></View>
          <View style={styles.sectionHeading}>
            <Text style={styles.eyebrow}>ALL SEASON</Text>
            <Text style={styles.sectionTitle}>Season standings</Text>
          </View>
        </View>
        {!seasonId || leaderboardLoading ? (
          <View style={styles.loadingRow}><ActivityIndicator color={colors.ink.field} /><Text style={styles.message}>Loading season standings…</Text></View>
        ) : leaderboardError ? (
          <Text style={styles.message}>Season standings could not be loaded.</Text>
        ) : seasonStandings.length ? (
          <View style={styles.seasonList}>
            <View style={styles.seasonHeader}>
              <Text style={styles.seasonRank}>#</Text>
              <Text style={[styles.metricHeader, styles.playerHeader]}>Player</Text>
              <Pressable
                accessibilityLabel="Sort by correct picks"
                accessibilityRole="button"
                accessibilityState={{ selected: activeSort === 'correct' }}
                onPress={() => setRequestedSort('correct')}
                style={styles.metricPressable}
                testID="season-standings-sort-correct"
              >
                <Text style={[styles.metricHeader, activeSort === 'correct' && styles.metricHeaderActive]}>Correct</Text>
              </Pressable>
              {showPointsSort ? (
                <Pressable
                  accessibilityLabel="Sort by points"
                  accessibilityRole="button"
                  accessibilityState={{ selected: activeSort === 'points' }}
                  onPress={() => setRequestedSort('points')}
                  style={styles.pointsPressable}
                  testID="season-standings-sort-points"
                >
                  <Text style={[styles.metricHeader, activeSort === 'points' && styles.metricHeaderActive]}>Pts</Text>
                </Pressable>
              ) : null}
            </View>
            {seasonStandings.map((entry) => {
              const isCurrentUser = entry.userId === user?.id;
              const correctActive = activeSort === 'correct';
              const pointsActive = activeSort === 'points';
              return (
                <View
                  key={entry.userId}
                  style={[styles.seasonRow, isCurrentUser && styles.seasonRowCurrent]}
                  testID={`season-standings-row-${entry.userId}`}
                >
                  {isCurrentUser ? <View pointerEvents="none" style={styles.currentUserRail} /> : null}
                  <Text style={styles.seasonRank}>#{entry.rank}</Text>
                  <View style={styles.seasonPlayer}>
                    <Avatar size={32} style={styles.avatar}>
                      <AvatarImage src={entry.user.profileImageUrl} />
                      <AvatarFallback style={styles.avatarFallback}><Text style={styles.avatarInitials}>{initials(entry.user)}</Text></AvatarFallback>
                    </Avatar>
                    <Text style={styles.playerName} numberOfLines={1}>{displayName(entry.user)}</Text>
                  </View>
                  <Text style={styles.correctValue}>
                    <Text style={[styles.correctCount, correctActive && styles.correctCountActive]}>{entry.totalCorrectPicks}</Text>
                    {entry.totalPicks > 0 ? <Text style={styles.correctTotal}>/{entry.totalPicks}</Text> : null}
                  </Text>
                  {showPointsSort ? (
                    <Text style={[styles.pointsTotal, pointsActive && styles.pointsTotalActive]}>{entry.totalPoints.toFixed(1)}</Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : (
          <Text style={styles.message}>Season standings will appear after scored picks are in.</Text>
        )}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionIcon}><CalendarDays size={19} color={colors.ink.field} /></View>
          <View style={styles.sectionHeading}>
            <Text style={styles.eyebrow}>THE WEEKLY HONORS</Text>
            <Text style={styles.sectionTitle}>Week-by-week winners</Text>
          </View>
        </View>
        {isLoading ? (
          <View style={styles.loadingRow}><ActivityIndicator color={colors.ink.field} /><Text style={styles.message}>Loading weekly winners…</Text></View>
        ) : leaderboardError || highlightsQuery.isError ? (
          <Text style={styles.message}>Weekly winners could not be loaded.</Text>
        ) : weeklyWinners.length ? (
          <View style={styles.weekList}>
            {weeklyWinners.map((week) => (
              <View key={week.id} style={styles.weekRow}>
                <View style={styles.weekLabelBlock}>
                  <Text style={styles.weekLabel}>{week.label}</Text>
                  <Text style={styles.weekMeta}>{week.winners.length > 1 ? 'TIED WINNERS' : 'WEEK WINNER'}</Text>
                </View>
                <View style={styles.winnerList}>
                  {week.winners.length > 1 ? (
                    <View style={styles.tiedWinnerGroup}>
                      <Text style={styles.tiedWinnerNames}>{week.winners.map(({ entry }) => displayName(entry.user)).join(' · ')}</Text>
                      <Text style={styles.points}>{week.winners[0]?.standing.points.toFixed(1)} pts each</Text>
                    </View>
                  ) : week.winners.map(({ entry, standing }) => (
                    <View key={entry.userId} style={styles.winner}>
                      <Avatar size={34} style={styles.avatar}>
                        <AvatarImage src={entry.user.profileImageUrl} />
                        <AvatarFallback style={styles.avatarFallback}><Text style={styles.avatarInitials}>{initials(entry.user)}</Text></AvatarFallback>
                      </Avatar>
                      <View style={styles.winnerCopy}>
                        <Text style={styles.playerName} numberOfLines={1}>{displayName(entry.user)}</Text>
                        <Text style={styles.winnerMeta}>{standing.correctPicks}/{standing.totalPicks} correct</Text>
                      </View>
                      <Text style={styles.points}>{standing.points.toFixed(1)} pts</Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.message}>Weekly winners will appear after a slate is complete and scored.</Text>
        )}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionIcon}><Award size={19} color={colors.ink.field} /></View>
          <View style={styles.sectionHeading}>
            <Text style={styles.eyebrow}>AROUND THE LEAGUE</Text>
            <Text style={styles.sectionTitle}>League-mates’ top achievements</Text>
          </View>
        </View>
        {highlightsQuery.isLoading ? (
          <View style={styles.loadingRow}><ActivityIndicator color={colors.ink.field} /><Text style={styles.message}>Finding badge-worthy seasons…</Text></View>
        ) : highlightsQuery.isError ? (
          <Text style={styles.message}>League-mate achievements could not be loaded.</Text>
        ) : highlightItems.length ? (
          <View style={styles.highlightList}>
            {highlightItems.map(({ award, badge, player }) => (
              <View key={`${award.userId}-${award.badgeKey}`} style={styles.highlightItem}>
                <View style={styles.highlightPlayer}>
                  <Avatar size={32} style={styles.avatar}>
                    <AvatarImage src={player.profileImageUrl} />
                    <AvatarFallback style={styles.avatarFallback}><Text style={styles.avatarInitials}>{initials(player)}</Text></AvatarFallback>
                  </Avatar>
                  <Text style={styles.playerName} numberOfLines={1}>{displayName(player)}</Text>
                </View>
                <AchievementBadge badge={badge} earned={award} compact />
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyHighlights}>
            <Trophy size={22} color={colors.text.secondary} />
            <Text style={styles.message}>Your league-mates’ season highlights will show up here as badges are earned.</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create(() => ({
  avatar: { backgroundColor: colors.ink.paper, borderColor: colors.cardBorder },
  avatarFallback: { backgroundColor: colors.ink.paperDark, borderWidth: 0 },
  avatarInitials: { color: colors.ink.field, fontFamily: typography.fontFamily.mono, fontSize: 11, fontWeight: '700' },
  correctCount: { color: colors.text.primary, fontFamily: typography.fontFamily.mono, fontSize: 14, fontWeight: '700' },
  correctCountActive: { color: colors.ink.field },
  correctTotal: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 11 },
  correctValue: { textAlign: 'right', width: 76 },
  currentUserRail: { backgroundColor: colors.ink.maroon, borderBottomLeftRadius: 8, borderTopLeftRadius: 8, bottom: 0, left: 0, position: 'absolute', top: 0, width: 4 },
  emptyHighlights: { alignItems: 'center', gap: 9, paddingHorizontal: 20, paddingVertical: 18 },
  eyebrow: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' },
  highlightItem: { gap: 8 },
  highlightList: { gap: 14 },
  highlightPlayer: { alignItems: 'center', flexDirection: 'row', gap: 9 },
  loadingRow: { alignItems: 'center', flexDirection: 'row', gap: 10, paddingVertical: 16 },
  message: { color: colors.text.secondary, fontFamily: typography.fontFamily.body, fontSize: 13, lineHeight: 19 },
  metricHeader: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 10, letterSpacing: 0.6, textAlign: 'right', textTransform: 'uppercase' },
  metricHeaderActive: { color: colors.ink.field, textDecorationLine: 'underline' },
  metricPressable: { alignItems: 'flex-end', justifyContent: 'center', width: 76 },
  page: { gap: 16, paddingBottom: 20, width: '100%' },
  playerHeader: { flex: 1, textAlign: 'left' },
  playerName: { color: colors.text.primary, flex: 1, fontFamily: typography.fontFamily.bodyMedium, fontSize: 14, fontWeight: '700' },
  points: { color: colors.ink.field, fontFamily: typography.fontFamily.mono, fontSize: 12, fontWeight: '700' },
  pointsPressable: { alignItems: 'flex-end', justifyContent: 'center', width: 52 },
  pointsTotal: { color: colors.text.primary, fontFamily: typography.fontFamily.mono, fontSize: 14, fontWeight: '700', textAlign: 'right', width: 52 },
  pointsTotalActive: { color: colors.ink.field },
  seasonHeader: { alignItems: 'center', flexDirection: 'row', gap: 8, paddingBottom: 2 },
  seasonList: { gap: 0 },
  seasonPlayer: { alignItems: 'center', flex: 1, flexDirection: 'row', gap: 9, minWidth: 0 },
  seasonRank: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 12, width: 32 },
  seasonRow: { alignItems: 'center', borderRadius: 8, borderTopColor: colors.cardBorder, borderTopWidth: 1, flexDirection: 'row', gap: 8, overflow: 'hidden', paddingVertical: 10, position: 'relative' },
  seasonRowCurrent: { backgroundColor: colors.warning[200], marginHorizontal: -6, paddingHorizontal: 6 },
  section: { backgroundColor: colors.ink.paper, borderColor: colors.cardBorder, borderRadius: 10, borderWidth: 1, gap: 14, padding: 16 },
  sectionHeader: { alignItems: 'center', flexDirection: 'row', gap: 11 },
  sectionHeading: { flex: 1, gap: 3 },
  sectionIcon: { alignItems: 'center', backgroundColor: colors.success[50], borderRadius: 8, height: 38, justifyContent: 'center', width: 38 },
  sectionTitle: { color: colors.text.primary, fontFamily: typography.fontFamily.display, fontSize: 18, fontWeight: '700' },
  tiedWinnerGroup: { gap: 6 },
  tiedWinnerNames: { color: colors.text.primary, fontFamily: typography.fontFamily.bodyMedium, fontSize: 13, fontWeight: '700', lineHeight: 18 },
  weekLabel: { color: colors.text.primary, fontFamily: typography.fontFamily.display, fontSize: 16, fontWeight: '700' },
  weekLabelBlock: { gap: 3, minWidth: 92 },
  weekList: { gap: 0 },
  weekMeta: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 9, letterSpacing: 0.7 },
  weekRow: { borderTopColor: colors.cardBorder, borderTopWidth: 1, flexDirection: 'row', gap: 12, paddingVertical: 13 },
  winner: { alignItems: 'center', flexDirection: 'row', gap: 9 },
  winnerCopy: { flex: 1, gap: 2 },
  winnerList: { flex: 1, gap: 9 },
  winnerMeta: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 10 },
}));
